import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ShieldAlert, UserPlus, Send, Trash2, Mail, Upload, FileText,
} from "lucide-react";

/**
 * Admin-only page: manage the roster of police officers who receive
 * the police-brief PDF, and trigger a broadcast send.
 */
export default function PoliceRoster() {
  const { user } = useAuth();
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", name: "", rank: "", station: "" });
  const [sending, setSending] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const csvInputRef = useRef(null);

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/police-roster");
      setRoster(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load roster");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") {
      fetchRoster();
    }
  }, [fetchRoster, user]);

  useEffect(() => {
    // clean up the blob URL when the component unmounts
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const openPreview = useCallback(async () => {
    if (roster.length === 0) return;
    try {
      const { data } = await api.get("/police-brief.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      toast.error("Could not load the brief for preview");
    }
  }, [roster.length]);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
    }
  }, [previewUrl]);

  if (user && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  const addOfficer = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    try {
      await api.post("/police-roster", form);
      toast.success(`Added ${form.email}`);
      setForm({ email: "", name: "", rank: "", station: "" });
      fetchRoster();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add officer");
    }
  };

  const removeOfficer = async (email) => {
    if (!window.confirm(`Remove ${email} from the roster?`)) return;
    try {
      await api.delete(`/police-roster/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      fetchRoster();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove");
    }
  };

  const sendToRoster = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/police-brief/send");
      if (data.sent) {
        toast.success(`Police brief mailed to ${data.recipients} officer(s)`);
      } else if (data.disabled) {
        toast.warning("Set BREVO_API_KEY on the backend to enable roster sends.");
      } else {
        toast.error(data.reason || "Send failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
      // always close the dialog once the request settles — success, warning,
      // rate-limit or transient error alike — to avoid a stuck-open modal.
      closePreview();
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // let the same file be re-selected later
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) {
      toast.error("Please upload a .csv file");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/police-roster/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { added = 0, updated = 0, errors = [] } = data;
      if (added || updated) {
        toast.success(
          `Import complete: ${added} added, ${updated} updated` +
            (errors.length ? `, ${errors.length} skipped` : ""),
        );
      } else if (errors.length) {
        toast.warning(`No officers imported. ${errors.length} row(s) skipped.`);
      } else {
        toast.warning("CSV had no valid rows");
      }
      if (errors.length) {
        const preview = errors.slice(0, 3)
          .map((er) => `row ${er.row}: ${er.reason}`).join(" · ");
        toast.info(`Skipped rows — ${preview}${errors.length > 3 ? " …" : ""}`);
      }
      fetchRoster();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="police-roster-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-red-700 flex items-center gap-1.5">
            <ShieldAlert className="h-4 w-4" /> Administration
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">
            Police roster
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Officers on this roster receive the latest Police Brief PDF each time
            you press <span className="font-medium text-slate-800">Send</span>. Only admins
            can view or edit the roster. Emails are logged in the audit trail.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="roster-csv-input"
            className="hidden"
            onChange={importCsv}
          />
          <Button
            data-testid="roster-import-btn"
            type="button"
            variant="outline"
            onClick={() => csvInputRef.current?.click()}
            disabled={importing}
            className="gap-1.5"
          >
            <Upload className="h-4 w-4" />
            {importing ? "Importing…" : "Import CSV"}
          </Button>
          <Button
            data-testid="send-roster-btn"
            onClick={openPreview}
            disabled={sending || roster.length === 0}
            className="bg-red-600 hover:bg-red-700 gap-1.5"
          >
            <Send className="h-4 w-4" /> Preview & send
          </Button>
        </div>
      </header>

      <Dialog
        open={previewOpen}
        onOpenChange={(v) => (v ? setPreviewOpen(true) : closePreview())}
      >
        <DialogContent
          className="max-w-3xl p-0 gap-0 overflow-hidden"
          data-testid="send-preview-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200">
            <DialogTitle className="flex items-center gap-2 text-lg font-display">
              <FileText className="h-5 w-5 text-red-600" />
              Preview brief before sending
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              This is the exact 4-page PDF (English, Telugu, Kannada, Marathi) each
              of the <span className="font-medium text-slate-700">{roster.length}</span>{" "}
              officer(s) on the roster will receive as an attachment.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-100 h-[62vh]" data-testid="send-preview-viewer">
            {previewUrl ? (
              <iframe
                title="Police brief preview"
                src={`${previewUrl}#toolbar=0&view=FitH`}
                className="w-full h-full border-0"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">
                Loading preview…
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-white">
            <Button
              variant="outline"
              data-testid="send-preview-cancel"
              onClick={closePreview}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              data-testid="send-preview-confirm"
              onClick={sendToRoster}
              disabled={sending}
              className="bg-red-600 hover:bg-red-700 gap-1.5"
            >
              <Send className="h-4 w-4" />
              {sending ? "Sending…" : `Send to ${roster.length} officer(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <form
        onSubmit={addOfficer}
        data-testid="roster-add-form"
        className="border border-slate-200 bg-white rounded-md p-5 grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end"
      >
        <div className="lg:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono block mb-1">
            Email
          </label>
          <Input
            data-testid="roster-email-input"
            type="email"
            placeholder="officer@police.gov.in"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono block mb-1">
            Name
          </label>
          <Input
            data-testid="roster-name-input"
            placeholder="Full name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono block mb-1">
            Rank
          </label>
          <Input
            data-testid="roster-rank-input"
            placeholder="e.g. Inspector"
            value={form.rank}
            onChange={(e) => setForm({ ...form, rank: e.target.value })}
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-mono block mb-1">
            Station
          </label>
          <div className="flex gap-2">
            <Input
              data-testid="roster-station-input"
              placeholder="e.g. Indiranagar"
              value={form.station}
              onChange={(e) => setForm({ ...form, station: e.target.value })}
            />
            <Button
              data-testid="roster-add-submit"
              type="submit"
              className="bg-slate-900 hover:bg-slate-800 gap-1.5 shrink-0"
            >
              <UserPlus className="h-4 w-4" /> Add
            </Button>
          </div>
        </div>
      </form>

      <section className="border border-slate-200 bg-white rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500" data-testid="roster-loading">
            Loading roster…
          </div>
        ) : roster.length === 0 ? (
          <div
            className="p-8 text-center text-sm text-slate-500 flex flex-col items-center gap-2"
            data-testid="roster-empty"
          >
            <Mail className="h-6 w-6 text-slate-400" />
            <div className="font-medium text-slate-700">No officers on the roster yet</div>
            <div className="text-xs">
              Add at least one officer above before you can send briefs.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-mono tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Officer</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Station</th>
                <th className="text-left px-4 py-2.5">Added</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {roster.map((r) => (
                <tr key={r.email} data-testid={`roster-row-${r.email}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{r.name || "—"}</div>
                    {r.rank && (
                      <div className="text-xs text-slate-500 font-mono">{r.rank}</div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-600 break-all">
                    {r.email}
                  </td>
                  <td className="px-4 py-2.5 text-slate-700">{r.station || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">
                    {r.added_at ? new Date(r.added_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      type="button"
                      data-testid={`roster-remove-${r.email}`}
                      onClick={() => removeOfficer(r.email)}
                      className="inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
